import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PageLayout6 from '../../../components/common/PageLayout/PageLayout6';
import ButtonBS from '../../../components/common/ButtonBS';
import Checkbox from '../../../components/common/CustomCheckbox';

import { submitConsents } from '../../../actions/consentActions';
import { cancelOperation } from '../../../actions/authActions';
import { PromptUserConsent as i18n } from '../../../middlewares/i18n';

import '../../../styles/PromptUserConsent/PromptUserConsent.css';

const PromptUserConsent = () => {
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [optInsValues, setOptInsValues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const dispatch = useDispatch();
  const consents = useSelector(state => state.consent.consents) || [];
  const _i18n = useSelector(state => state.i18n);
  
  // Filter consent options based on missing consents from API
  // Show consents if they are required and have consent_value false, or optional consents if they have no last_seen_at
  // Sort by required first, then optional
  const filteredConsents = consents
    .filter(consent => {
      if (consent.is_required) {
        // Required consent: missing if not consented (regardless of last_seen_at)
        return consent.has_consent === false;
      } else {
        // Optional consent: missing only if never seen
        return consent.last_seen_at === null;
      }
    })
    .sort((a, b) => {
      // Required consents first (true comes before false)
      if (a.is_required && !b.is_required) return -1;
      if (!a.is_required && b.is_required) return 1;
      return 0;
    });

  // Initialize on mount
  useEffect(() => {
    // Initialize optInsValues to true for all consents
    setOptInsValues(new Array(filteredConsents.length).fill(true));
    // Set loading to false
    setIsLoading(false);
  }, [filteredConsents.length]);

  const handleCancel = () => {
    dispatch(cancelOperation());
  };

  const handleContinue = async () => {
    // Check if all required consents are selected
    const requiredConsents = filteredConsents.filter(consent => consent.is_required);    
    const allRequiredSelected = requiredConsents.every(consent => {
      const index = filteredConsents.findIndex(c => c.consent_type === consent.consent_type);
      return index !== -1 && optInsValues[index];
    });
    
    // If all required consents are not selected, show alert
    if (!allRequiredSelected) {
      setAlertMessage('Please select all required consents to continue.');
      setIsAlertVisible(true);
      return;
    }

    // Only submit consents that are actually being shown (not previously seen)
    const allConsentTypes = filteredConsents.map((consent, index) => ({
      type: consent.consent_type,
      value: optInsValues[index] || false
    }));

    // Submit consents - the action handles all loading, error handling, and navigation
    dispatch(submitConsents(allConsentTypes));
  };

  const handleOptIn = (index) => (value) => {
    const newOptInsValues = [...optInsValues];
    newOptInsValues[index] = value;
    setOptInsValues(newOptInsValues);
    setIsAlertVisible(false);
    setAlertMessage('');
  };

  // Check if all required consents are selected
  const isContinueDisabled = () => {
    const requiredConsents = filteredConsents.filter(consent => consent.is_required);
    
    return !requiredConsents.every(consent => {
      const index = filteredConsents.findIndex(c => c.consent_type === consent.consent_type);
      return index !== -1 && optInsValues[index];
    });
  };

  if (isLoading) {
    return (
      <PageLayout6>
        <div className="modal-user-consent">
          <div className="phone-icon modal-user-consent__icon" />
          <h4 className="modal-user-consent__title">{i18n[_i18n.language.current].loading}</h4>
          <div className="modal-user-consent__subtitle">{i18n[_i18n.language.current].pleaseWait}</div>
        </div>
      </PageLayout6>
    );
  }

  // If no missing consents to show, show loading while useEffect handles navigation
  if (filteredConsents.length === 0) {
    return null;
  }

  return (
    <PageLayout6>
      <div className="modal-user-consent">
        <div className="phone-icon modal-user-consent__icon" />
        <h4 className="modal-user-consent__title">{i18n[_i18n.language.current].title}</h4>
        <div className="modal-user-consent__subtitle">{i18n[_i18n.language.current].subtitle}</div>

        <div className="modal-user-consent__opt-ins">
          {filteredConsents.map((consent, index) => (
            <div key={`modal-user-consent__opt-in-${index}`} className="modal-user-consent__opt-in">
              <div className="modal-user-consent__opt-in-col">
                <Checkbox 
                  onChange={handleOptIn(index)} 
                  id={`user-consent-optin-${consent.consent_type}-${index}`} 
                  initialChecked={optInsValues[index] || false}
                />
              </div>
              <div className="modal-user-consent__opt-in-col">
                <div className="modal-user-consent__opt-in-title">
                  {consent.display_name}
                  {consent.is_required && (
                    <span style={{ color: 'red', marginLeft: '8px' }}>- required</span>
                  )}
                </div>
                <div className="modal-user-consent__opt-in-subtitle">{consent.description}</div>
              </div>
            </div>
          ))}
        </div>

        {isAlertVisible && (
          <div className="modal-user-consent-alert">
            <div className="error-icon modal-user-consent-alert__icon" />
            <div className="modal-user-consent-alert__title">
              {alertMessage || i18n[_i18n.language.current].alertTitle}
            </div>
          </div>
        )}

        <hr className="modal-user-consent__hr" />

        <div className="modal-user-consent__text-bottom">{i18n[_i18n.language.current].messageRateApply}</div>

        <div className="modal-user-consent__buttons">
          <ButtonBS v2 className="v2--red" content={i18n[_i18n.language.current].cancel} onClick={handleCancel}/>
          <ButtonBS 
            v2 
            className={isContinueDisabled() ? "v2--gray" : "v2--red"} 
            content={i18n[_i18n.language.current].continue}
            onClick={handleContinue}
            disabled={isContinueDisabled()}
          />
        </div>
      </div>
    </PageLayout6>
  );
};

export default PromptUserConsent;